import { useEffect } from 'react'
import { useLocation, useParams } from 'react-router'
import { getActiveSpan, getRootSpan, spanToJSON } from '@sentry/react'

/**
 * URL のパスとルートパラメータから、ルートパターン（`/deal/:dealId`）を復元する。
 *
 * React Router はマッチしたルートのパターン自体を返さないため、パスの中でパラメータの値と
 * 一致するセグメントを `:キー名` に置き換えて組み立てる。
 * catch-all（`*`）は複数セグメントにまたがるので、先に末尾をまとめて `*` にする。
 *
 * 名前付きパラメータは**末尾のセグメントから**照合し、1 つのキーは 1 回だけ使う。
 * `params` のキーは親ルートから子ルートの順に並び、パラメータは静的セグメントより後ろに来ることが多いため、
 * 後ろから見るほうが `/companies/5/staffs/5`（同じ値の 2 パラメータ）や `/deal/deal`（値が静的セグメントと同じ）を
 * 正しい位置に割り当てられる。`pathname` は URL エンコードされたまま・`params` はデコード済みなので、
 * セグメントはデコードしてから比べる。
 */
export const toRoutePattern = (
  pathname: string,
  params: Readonly<Record<string, string | undefined>>,
): string => {
  let path = pathname

  const splat = params['*']
  if (splat) {
    const index = path.lastIndexOf(splat)
    if (index >= 0 && index + splat.length === path.length) {
      path = `${path.slice(0, index)}*`
    }
  }

  const named = Object.entries(params).filter(
    (entry): entry is [string, string] => entry[0] !== '*' && !!entry[1],
  )
  if (named.length === 0) return path || '/'

  const unused = [...named]
  const segments = path.split('/')
  for (let i = segments.length - 1; i >= 0; i--) {
    const decoded = safeDecode(segments[i])
    // `findLastIndex`（ES2023）は使わない。描画のたびに通る計測用の処理で、古い端末の例外が画面全体を巻き込むため
    let hitIndex = -1
    for (let j = unused.length - 1; j >= 0; j--) {
      if (unused[j][1] === decoded) {
        hitIndex = j
        break
      }
    }
    if (hitIndex < 0) continue
    segments[i] = `:${unused[hitIndex][0]}`
    unused.splice(hitIndex, 1)
  }
  return segments.join('/') || '/'
}

const safeDecode = (segment: string): string => {
  try {
    return decodeURIComponent(segment)
  } catch {
    return segment
  }
}

/**
 * Sentry のページロード / 画面遷移の計測に、ルートパターンを名前として与える。
 *
 * SDK v11 は span 名を低カーディナリティにする方針に変わり、**ルートパターンを知らない場合は
 * `Pageload` / `Navigation` という固定名**になった（v10 までは生の URL パスを名前にしていた）。
 * 固定名のままだと Sentry のパフォーマンス画面でページごとの傾向が見えなくなるため、
 * ルートパターンを明示的に渡す。
 *
 * SDK 同梱の `reactRouterBrowserTracingIntegration` はルート定義を自前で組む構成
 * （declarative / data router）向けで、企業画面・管理画面のようにルートをファイル配置から
 * 生成する構成（framework mode）では使えない。`@sentry/react-router` はサーバー描画が前提
 * （peerDependencies に `@react-router/node`）で、SPA 構成の手順が無い。そのため
 * React Router の `useParams()` と Sentry の公開 API だけで同じ結果を作る。
 *
 * 施主画面（customer）は React Router を使っていないため対象外。
 */
export const useSentryRouteName = () => {
  // `key` は遷移のたびに変わる。パターンが同じ遷移（`/deal/1` → `/deal/2`、検索条件だけの変更）でも
  // SDK は新しい navigation span を作るので、パターンではなく遷移ごとに名前を付け直す
  const { pathname, key } = useLocation()
  // `useParams()` は現在のルートのパラメータを返す。`useMatches()` ではなくこちらを使うのは、
  // `useMatches()` がデータルーター（`RouterProvider`）配下でしか呼べず、
  // 宣言的なルーター（テストや Storybook の `MemoryRouter`）で例外になるため
  const params = useParams()
  const pattern = toRoutePattern(pathname, params)

  useEffect(() => {
    const active = getActiveSpan()
    const root = active ? getRootSpan(active) : undefined
    if (!root) return

    // 計測の起点（ページロード / 画面遷移）以外の span の名前は変えない。
    // v11 の `spanToJSON` は op を属性（`sentry.op`）として返す（旧 `op` プロパティは廃止）
    const op = spanToJSON(root).attributes?.['sentry.op']
    if (op !== 'pageload' && op !== 'navigation') return

    root.updateName(pattern)
    // Sentry 側がルートを参照するときの属性。名前と両方に載せる
    root.setAttribute('http.route', pattern)
    root.setAttribute('url.template', pattern)
  }, [pattern, key])
}
